import { useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import type { WorkflowStatus } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import { CreateWorkflowModal } from "@/components/workflows/create-workflow-modal";
import { WorkflowBuilder } from "@/components/workflows/workflow-builder";
import {
  useRetryWorkflowEnrollments,
  useUpdateWorkflow,
  useWorkflow,
  useWorkflowEnrollments,
  useWorkflowStatusAction,
  useWorkflows,
} from "@/hooks/useWorkflows";
import { useDebouncedValue } from "@/hooks/usePagination";

function triggerLabel(type: string): string {
  return type.replace(/_/g, " ");
}

export function WorkflowsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<WorkflowStatus | "all">("all");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const [createOpen, setCreateOpen] = useState(false);
  const listQuery = useWorkflows({
    status,
    q: debouncedQ || undefined,
  });
  const statusMut = useWorkflowStatusAction();
  const workflows = listQuery.data?.workflows ?? [];

  return (
    <div className="space-y-5 pb-10">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Workflows</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Automate enrollments, tasks, and lists with triggers and actions.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
        >
          Create a workflow
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            ["all", "All"],
            ["active", "Active"],
            ["draft", "Draft"],
            ["archived", "Archived"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setStatus(id)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm",
              status === id
                ? "bg-brand text-brand-foreground"
                : "border border-border text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search workflows…"
          className="ml-1 w-48 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>

      <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
        {listQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          </div>
        ) : workflows.length === 0 ? (
          <div className="space-y-3 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              No workflows yet. Create from scratch, a template, or the AI
              assistant.
            </p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="text-sm text-brand hover:underline"
            >
              Create a workflow
            </button>
          </div>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Trigger</th>
                <th className="px-3 py-2 font-medium">Enrolled</th>
                <th className="px-3 py-2 font-medium">Completed</th>
                <th className="px-3 py-2 font-medium">Failed</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {workflows.map((w) => (
                <tr
                  key={w.workflowId}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/40"
                  onClick={() => navigate(`/workflows/${w.workflowId}`)}
                >
                  <td className="px-3 py-2.5 font-medium">{w.name}</td>
                  <td className="px-3 py-2.5 capitalize">{w.status}</td>
                  <td className="px-3 py-2.5 capitalize text-muted-foreground">
                    {triggerLabel(w.trigger.type)}
                  </td>
                  <td className="px-3 py-2.5">{w.stats.enrolled}</td>
                  <td className="px-3 py-2.5">{w.stats.completed}</td>
                  <td className="px-3 py-2.5">{w.stats.failed}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {new Date(w.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      className="text-xs text-brand hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        statusMut.mutate({
                          workflowId: w.workflowId,
                          action: "clone",
                        });
                      }}
                    >
                      Clone
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <CreateWorkflowModal onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

export function WorkflowDetailLayout() {
  const { workflowId = "" } = useParams();
  const wfQuery = useWorkflow(workflowId);
  const statusMut = useWorkflowStatusAction();
  const workflow = wfQuery.data?.workflow;

  if (wfQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        Workflow not found.{" "}
        <Link to="/workflows" className="text-brand hover:underline">
          Back
        </Link>
      </div>
    );
  }

  const tabs = [
    { to: `/workflows/${workflowId}`, label: "Overview", end: true },
    { to: `/workflows/${workflowId}/edit`, label: "Builder", end: true },
    { to: `/workflows/${workflowId}/enrollment`, label: "Enrollment", end: true },
    { to: `/workflows/${workflowId}/settings`, label: "Settings", end: true },
  ];

  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/workflows"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Workflows
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {workflow.name}
          </h1>
          <p className="mt-1 text-sm capitalize text-muted-foreground">
            {workflow.status} · {triggerLabel(workflow.trigger.type)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {workflow.status !== "active" ? (
            <button
              type="button"
              onClick={() =>
                statusMut.mutate({
                  workflowId,
                  action: "activate",
                })
              }
              className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
            >
              Activate
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                statusMut.mutate({
                  workflowId,
                  action: "deactivate",
                })
              }
              className="rounded-md border border-border px-3 py-1.5 text-sm"
            >
              Deactivate
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              statusMut.mutate({ workflowId, action: "archive" })
            }
            className="rounded-md border border-border px-3 py-1.5 text-sm"
          >
            Archive
          </button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet context={{ workflow }} />
    </div>
  );
}

export function WorkflowOverviewPage() {
  const { workflowId = "" } = useParams();
  const wfQuery = useWorkflow(workflowId);
  const workflow = wfQuery.data?.workflow;
  if (!workflow) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {[
        ["Enrolled", workflow.stats.enrolled],
        ["Completed", workflow.stats.completed],
        ["Failed", workflow.stats.failed],
      ].map(([label, value]) => (
        <div
          key={String(label)}
          className="rounded-lg border border-border p-4"
        >
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="mt-1 text-2xl font-semibold">{value}</p>
        </div>
      ))}
      <div className="sm:col-span-3 rounded-lg border border-border p-4 text-sm">
        <p className="font-medium">Target</p>
        <p className="text-muted-foreground capitalize">{workflow.targetType}</p>
        <p className="mt-3 font-medium">Steps</p>
        <p className="text-muted-foreground">
          {workflow.nodes.length} action block
          {workflow.nodes.length === 1 ? "" : "s"}
        </p>
        {workflow.description && (
          <>
            <p className="mt-3 font-medium">Description</p>
            <p className="text-muted-foreground">{workflow.description}</p>
          </>
        )}
        <Link
          to={`/workflows/${workflowId}/edit`}
          className="mt-4 inline-block text-brand hover:underline"
        >
          Edit workflow
        </Link>
      </div>
    </div>
  );
}

export function WorkflowEditPage() {
  const { workflowId = "" } = useParams();
  const wfQuery = useWorkflow(workflowId);
  if (wfQuery.isLoading || !wfQuery.data?.workflow) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }
  return <WorkflowBuilder workflow={wfQuery.data.workflow} />;
}

export function WorkflowEnrollmentPage() {
  const { workflowId = "" } = useParams();
  const enrollQuery = useWorkflowEnrollments(workflowId);
  const retryMut = useRetryWorkflowEnrollments();
  const enrollments = enrollQuery.data?.enrollments ?? [];
  const failed = useMemo(
    () => enrollments.filter((e) => e.status === "failed"),
    [enrollments],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Run history for contacts, companies, or deals processed by this
          workflow.
        </p>
        {failed.length > 0 && (
          <button
            type="button"
            disabled={retryMut.isPending}
            onClick={() =>
              retryMut.mutate({ workflowId })
            }
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            Retry failed records ({failed.length})
          </button>
        )}
      </div>
      <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
        {enrollQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          </div>
        ) : enrollments.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No enrollments yet. Enroll from People or wait for the trigger.
          </div>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b border-border bg-muted/30 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Record</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Enrolled by</th>
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.map((e) => (
                <tr
                  key={e.enrollmentId}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-3 py-2.5">
                    {e.recordName || e.recordId}
                  </td>
                  <td className="px-3 py-2.5 capitalize">{e.status}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {e.enrolledBy}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {new Date(e.enrolledAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-destructive">
                    {e.error || e.failureReason || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function WorkflowSettingsPage() {
  const { workflowId = "" } = useParams();
  const wfQuery = useWorkflow(workflowId);
  const updateMut = useUpdateWorkflow();
  const workflow = wfQuery.data?.workflow;
  const [days, setDays] = useState<number | null>(null);
  const [visibility, setVisibility] = useState<"private" | "team" | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (!workflow) return null;
  const reEnrollmentDays = days ?? workflow.settings.reEnrollmentDays;
  const vis = visibility ?? workflow.settings.visibility;

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <label className="block text-sm">
        <span className="text-muted-foreground">Re-enrollment cooldown (days)</span>
        <input
          type="number"
          min={0}
          value={reEnrollmentDays}
          onChange={(e) => setDays(Number(e.target.value))}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="text-muted-foreground">Visibility</span>
        <select
          value={vis}
          onChange={(e) => setVisibility(e.target.value as "private" | "team")}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
        >
          <option value="private">Restricted (private)</option>
          <option value="team">Everyone at company</option>
        </select>
      </label>
      <button
        type="button"
        disabled={updateMut.isPending}
        onClick={async () => {
          setMessage(null);
          try {
            await updateMut.mutateAsync({
              workflowId,
              patch: {
                settings: {
                  reEnrollmentDays,
                  visibility: vis,
                },
              },
            });
            setMessage("Saved");
          } catch (err) {
            setMessage(err instanceof Error ? err.message : "Save failed");
          }
        }}
        className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground"
      >
        Save settings
      </button>
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
    </div>
  );
}
